<template>
  <TenderStep
    :current-step="currentStep"
    :total-steps="totalSteps"
    :error-transition="false"
  >
    <template slot="header">
      <div class="question-big">
        {{ $t("requestTender.jobDescription.question") }}
      </div>
    </template>

    <template v-if="!newTender.descriptionHint.value?.length" slot="subHeader">
      {{ $t("requestTender.jobDescription.describeTask") }}
    </template>
    <template v-else slot="subHeader">
      <transition name="fade">
        <div v-if="automaticNextQuestion" class="text-base text-gray-600 mb-3">
          {{ $t("requestTender.jobDescription.questionHintPrompt") }}
        </div>
      </transition>
      <div
        class="bg-primary-light/10 p-3 md:p-4 rounded-xl text-primary relative font-semibold text-base md:text-lg pr-14"
      >
        <transition name="fade" mode="out-in" appear>
          <div :key="questionIndex">
            {{ currentHintQuestion }}
          </div>
        </transition>
        <button
          type="button"
          class="grid absolute right-3 top-1/2 -translate-y-1/2 text-white bg-primary-light rounded-full w-9 h-9 place-items-center cursor-pointer hover:brightness-110 active:brightness-90"
          :aria-label="$t('requestTender.next')"
          @click="cycleHintQuestion"
        >
          <IconComponent icon="arrow-right" />
        </button>
      </div>
    </template>

    <div class="relative">
      <JoszakiTextArea
        ref="jobDescription"
        v-model="newTender.jobDescription.value"
        name="jobDescription"
        :placeholder="$t('requestTender.jobDescription.placeholder')"
        :show-error-msg="false"
        auto-size
        autofocus
      />
      <span
        v-if="isValid && descriptionLength > 0"
        class="absolute right-3 top-3 text-positive pointer-events-none"
      >
        <IconComponent class="fa-lg" icon="check" />
      </span>
    </div>

    <div v-if="!isValid && descriptionLength > 0" class="text-red-700 mt-2">
      <small v-if="descriptionLength < minDescriptionLength">
        <i18n path="requestTender.jobDescription.validation.minLength">
          <template #remaining>
            <strong class="text-red-700">
              {{
                $t("requestTender.jobDescription.validation.remaining", {
                  remaining: descriptionCharsNeeded,
                })
              }}
            </strong>
          </template>
          <template #minLength>
            {{ minDescriptionLength }}
          </template>
        </i18n>
      </small>
      <small v-if="descriptionLength > maxDescriptionLength">
        {{
          $t("requestTender.jobDescription.validation.maxLength", {
            maxLength: maxDescriptionLength,
          })
        }}
      </small>
    </div>

    <template slot="footer">
      <WizardNextButton :disabled="!isValid" @click="emitNext()" />
    </template>
  </TenderStep>
</template>

<script>
import { toRefs } from "@nuxtjs/composition-api";
import WizardNextButton from "./WizardNextButton.vue";
import TenderStep from "@/components/TenderStep";
import { useNewTender } from "~/composables/tender/useNewTender";
import { useTrackWizardClick } from "~/composables/tracking/useTrackWizardClick";

export default {
  name: "JobDescriptionStepModule",
  components: { TenderStep, WizardNextButton },
  props: {
    currentStep: { type: Number, required: true },
    totalSteps: { type: Number, required: true },
    moduleSlug: { type: String, default: "job-description" },
  },
  emits: ["next"],
  setup(props) {
    const newTender = useNewTender();
    const { trackNextAttempt } = useTrackWizardClick(toRefs(props));
    return {
      trackNextAttempt,
      newTender,
      minDescriptionLength: newTender.minDescriptionLength,
      maxDescriptionLength: newTender.maxDescriptionLength,
    };
  },
  data() {
    return {
      automaticNextQuestion: true,
      questionIndex: 0,
    };
  },
  computed: {
    descriptionLength() {
      return this.newTender.jobDescription.value?.length ?? 0;
    },
    isValid() {
      const len = this.descriptionLength;
      return (
        len >= this.minDescriptionLength && len <= this.maxDescriptionLength
      );
    },
    descriptionCharsNeeded() {
      return this.minDescriptionLength - this.descriptionLength;
    },
    currentHintQuestion() {
      return this.newTender.descriptionHint.value?.[this.questionIndex]
        ?.question;
    },
  },
  methods: {
    cycleHintQuestion() {
      const total = this.newTender.descriptionHint.value?.length ?? 0;
      if (!total) return;
      this.questionIndex =
        this.questionIndex + 1 === total ? 0 : this.questionIndex + 1;
      this.automaticNextQuestion = false;
    },
    emitNext(trigger = "click") {
      this.trackNextAttempt({ trigger, disabled: !this.isValid });
      if (!this.isValid) return;
      this.newTender.saveToStorage();
      this.$emit("next", { field: "jobDescription" });
    },
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
